
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useTags } from "@/hooks/useTags";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { colorOptions } from "@/utils/colorOptions";

const formSchema = z.object({
  name: z.string().min(1, "Tag name is required").max(50, "Tag name cannot exceed 50 characters"),
  description: z.string().max(200, "Description cannot exceed 200 characters").optional(),
  color: z.string().regex(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, "Must be a valid hex color code")
});

interface EditTagDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tagId?: string;
  initialData?: {
    name: string;
    description?: string;
    color: string;
  };
}

const EditTagDialog = ({ open, onOpenChange, tagId, initialData }: EditTagDialogProps) => {
  const { updateTag, createTag, isLoading } = useTags();
  const [selectedColor, setSelectedColor] = useState(initialData?.color || colorOptions[0].value);
  const isEditing = !!tagId;
  
  // Debug logs to track props
  useEffect(() => {
    console.log("EditTagDialog props:", { open, tagId, initialData });
  }, [open, tagId, initialData]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: initialData?.name || "",
      description: initialData?.description || "",
      color: initialData?.color || colorOptions[0].value
    }
  });

  // Update form when initialData changes
  useEffect(() => {
    if (initialData) {
      console.log("Updating form with initial data:", initialData);
      form.reset({
        name: initialData.name,
        description: initialData.description || "",
        color: initialData.color
      });
      setSelectedColor(initialData.color);
    } else {
      form.reset({
        name: "",
        description: "",
        color: colorOptions[0].value
      });
      setSelectedColor(colorOptions[0].value);
    }
  }, [initialData, form]);

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    console.log("Form submitted:", data);
    try {
      if (isEditing && tagId) {
        console.log("Updating tag:", tagId, data);
        const success = await updateTag(tagId, data);
        if (success) {
          toast.success("Tag updated successfully");
          onOpenChange(false);
        }
      } else {
        console.log("Creating new tag:", data);
        const tag = await createTag(data.name, data.color, data.description);
        if (tag) {
          toast.success("Tag created successfully");
          onOpenChange(false);
        }
      }
    } catch (error) {
      console.error("Error saving tag:", error);
      toast.error("Failed to save tag");
    }
  };

  const handleColorSelect = (color: string) => {
    form.setValue("color", color);
    setSelectedColor(color);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">{isEditing ? "Edit Tag" : "Create New Tag"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tag Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter tag name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Enter a brief description of this tag (optional)" 
                      className="resize-none" 
                      rows={3}
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="space-y-2">
              <FormLabel>Tag Color</FormLabel>
              <div className="grid grid-cols-5 gap-4 md:grid-cols-10">
                {colorOptions.map((color) => (
                  <div
                    key={color.name}
                    className={`h-11 w-11 rounded-lg cursor-pointer flex items-center justify-center ${
                      selectedColor === color.value ? "ring-2 ring-offset-2 ring-clustr-blue" : ""
                    }`}
                    style={{ backgroundColor: color.value }}
                    onClick={() => handleColorSelect(color.value)}
                    title={color.name}
                  >
                    {selectedColor === color.value && (
                      <div className="bg-white rounded-full p-1">
                        <Check className="h-3 w-3 text-black" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <Separator className="my-4" />
            
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Saving..." : isEditing ? "Save Changes" : "Create Tag"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default EditTagDialog;
